// src/pos-app/StationSettingsSheet.js
//
// F12a Task 5.2 — manager-gated station settings sheet for the native POS app.
// How a manager reconfigures an enrolled device (station, KDS queue) or
// resets its enrollment, without reinstalling. Two stages:
//
//   1. Manager gate — 6-digit PIN pad AND the password fallback both POST
//      DIRECTLY on the axios instance (/auth/employee-login and
//      /auth/tenant-login respectively) rather than through
//      authService.employeeLogin / authService.tenantLogin / AuthContext.login
//      — every one of those persists token/role/tenant state to localStorage
//      and would silently replace the device's active operator session, even
//      on a rejected or non-manager credential. Both gate paths only inspect
//      the response (role must be manager/admin, tenant must match for the
//      password path) and then discard it — the device's current session is
//      never read or written by this component.
//   2. Settings — station / KDS queue segmented controls, saved through
//      StationContext, plus a destructive "reset enrollment" action that
//      returns the device to the EnrollmentScreen. There is deliberately no
//      customer-facing backend (prod/staging) toggle here — a customer till
//      must never be able to point itself at staging. Internal QC builds
//      target staging at build time via REACT_APP_API_URL instead (see
//      src/services/apiBaseUrl.js's resolver).
//
// Visual language: "Aurora Glass" (docs/mockups/f12a/cashier-station.html —
// manager gate + station settings modals): glass panel over a blurred scrim,
// SOLID keypad keys and segmented controls, all colors from the themeable
// --c-* vars so light/dark flip automatically. RTL-safe via logical
// properties; Escape and the close button both dismiss.
// Portal rationale — `.sts-overlay` is `position:fixed`, and the stations
// render this sheet inside their glass chrome. An ancestor with a non-`none`
// backdrop-filter becomes the containing block for fixed descendants, so the
// overlay would size itself to that chrome rather than the viewport. Portaled
// to document.body for the same reason components/common/Modal.js and
// components/pos/ManagerGate.js are.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useStation } from '../station/StationContext';
import { STATIONS, DEFAULT_PRINTER_IDS } from '../station/stationConfig';
import { FONT } from '../styles/theme';
import StationSettingsPrinter from './StationSettingsPrinter';
import ManagerGate, { isManagerRole } from '../components/pos/ManagerGate';
import { categoryService } from '../services/api';


/* ------------------------------------------------------------------ */
/* Stylesheet — uses the app's --c-* theme vars so both themes work.   */
/* ------------------------------------------------------------------ */
const CSS = `
.sts-overlay{position:fixed;inset:0;z-index:80;display:flex;align-items:center;justify-content:center;box-sizing:border-box;
  padding:calc(20px + var(--safe-top)) 20px calc(20px + var(--safe-bottom));
  font-family:${FONT};-webkit-font-smoothing:antialiased;}
.sts-scrim{position:absolute;inset:0;background:rgba(2,8,23,.45);
  -webkit-backdrop-filter:blur(10px);backdrop-filter:blur(10px);animation:stsFade .2s ease;}
.sts-panel{position:relative;width:400px;max-width:100%;max-height:100%;overflow-y:auto;box-sizing:border-box;
  border-radius:20px;padding:22px;
  background:var(--c-surface);
  background:color-mix(in srgb, var(--c-surface) 84%, transparent);
  -webkit-backdrop-filter:blur(22px) saturate(1.5);backdrop-filter:blur(22px) saturate(1.5);
  border:1px solid var(--c-border);
  border-color:color-mix(in srgb, var(--c-border) 55%, transparent);
  box-shadow:0 18px 50px rgba(2,8,23,.28);
  animation:stsIn .25s ease;}
@keyframes stsIn{from{opacity:0;transform:translateY(14px) scale(.98);}to{opacity:1;transform:translateY(0) scale(1);}}
@keyframes stsFade{from{opacity:0;}to{opacity:1;}}
@keyframes stsShake{0%,100%{transform:translateX(0);}25%{transform:translateX(-7px);}75%{transform:translateX(7px);}}
@keyframes stsSpin{to{transform:rotate(360deg);}}
@media (prefers-reduced-motion: reduce){
  .sts-panel,.sts-scrim{animation:none !important;}
  .sts-dots.err{animation:none !important;}
}

.sts-head{display:flex;align-items:center;justify-content:space-between;gap:10px;margin-bottom:4px;}
.sts-head h3{font-size:16px;font-weight:800;letter-spacing:-.2px;color:var(--c-ink);margin:0;}
.sts-close{width:44px;height:44px;border-radius:12px;flex:none;cursor:pointer;
  display:flex;align-items:center;justify-content:center;color:var(--c-muted);
  background:var(--c-faint);border:1px solid var(--c-border);
  transition:transform .12s ease, background .15s ease;}
.sts-close:active{transform:scale(.92);background:var(--c-border);}
.sts-sub{font-size:12.5px;color:var(--c-muted);font-weight:600;margin:0 0 16px;line-height:1.5;}

.sts-error{display:flex;gap:9px;align-items:flex-start;padding:11px 13px;border-radius:12px;margin-bottom:14px;
  background:var(--c-redLight);border:1px solid var(--c-redBorder);color:var(--c-redText);
  font-size:12.5px;font-weight:600;line-height:1.5;animation:stsFade .2s ease;}
.sts-error svg{flex:none;margin-top:1px;}

/* ---- gate: PIN pad ---- */
.sts-dots{display:flex;direction:ltr;justify-content:center;gap:13px;margin:6px 0 18px;height:15px;align-items:center;}
.sts-dot{width:13px;height:13px;border-radius:50%;border:2px solid var(--c-mutedLight);background:transparent;
  box-sizing:border-box;transition:all .15s ease;}
.sts-dot.fill{background:var(--c-green);border-color:var(--c-green);transform:scale(1.12);}
.sts-dots.err{animation:stsShake .3s ease;}
.sts-keypad{display:grid;direction:ltr;grid-template-columns:repeat(3,1fr);gap:10px;width:100%;}
.sts-key{min-height:58px;border-radius:14px;cursor:pointer;touch-action:manipulation;
  background:var(--c-surface);color:var(--c-ink);border:1px solid var(--c-border);
  font-size:21px;font-weight:700;font-family:inherit;
  box-shadow:0 1px 2px rgba(2,8,23,.07);
  display:flex;align-items:center;justify-content:center;
  transition:transform .1s ease, background .12s ease;}
.sts-key:active{transform:scale(.93);background:var(--c-faint);}
.sts-key:disabled{opacity:.55;cursor:default;transform:none;}
.sts-key.fn{color:var(--c-muted);}
.sts-key.go{background:var(--c-green);border-color:var(--c-green);color:#fff;
  box-shadow:0 4px 12px color-mix(in srgb, var(--c-green) 40%, transparent);}
.sts-key.go:active{background:var(--c-greenDark);}
/* Keypad is LTR, so its glyphs must not be mirrored. */

.sts-switch{display:block;width:100%;margin-top:16px;padding:12px 8px;border:none;background:transparent;
  border-radius:11px;cursor:pointer;text-align:center;
  color:var(--c-muted);font-size:13px;font-weight:700;font-family:inherit;text-decoration:underline;
  text-underline-offset:3px;text-decoration-color:var(--c-mutedLight);
  transition:color .15s ease, transform .12s ease;}
.sts-switch:active{transform:scale(.97);color:var(--c-text);}

/* ---- gate: password fallback ---- */
.sts-field{margin-bottom:13px;}
.sts-label{display:block;font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:.8px;color:var(--c-muted);margin-bottom:7px;}
.sts-input{width:100%;min-height:48px;padding:0 15px;box-sizing:border-box;border-radius:13px;
  border:1.5px solid var(--c-border);background:var(--c-surface);color:var(--c-text);
  font-size:15px;font-weight:600;font-family:inherit;
  transition:border-color .15s ease, box-shadow .15s ease;}
.sts-input::placeholder{color:var(--c-mutedLight);font-weight:500;}
.sts-input:focus{outline:none;border-color:var(--c-green);box-shadow:0 0 0 3px var(--c-greenLight);}

.sts-btn{width:100%;min-height:50px;border:none;border-radius:14px;cursor:pointer;margin-top:4px;
  display:flex;align-items:center;justify-content:center;gap:9px;
  background:linear-gradient(180deg,var(--c-green) 0%,var(--c-greenDark) 100%);
  color:#fff;font-size:15px;font-weight:800;letter-spacing:-.2px;font-family:inherit;
  box-shadow:0 5px 15px color-mix(in srgb, var(--c-green) 35%, transparent), inset 0 1px 0 rgba(255,255,255,.18);
  transition:transform .15s ease, filter .15s ease, opacity .15s ease;}
.sts-btn:active{transform:scale(.97);filter:brightness(1.08);}
.sts-btn:disabled{opacity:.45;cursor:default;transform:none;filter:none;box-shadow:none;}
.sts-spin{animation:stsSpin .8s linear infinite;}

/* ---- settings ---- */
.sts-row{margin-bottom:15px;}
.sts-row.off{opacity:.45;pointer-events:none;}
.sts-seg{display:grid;grid-template-columns:repeat(2,1fr);gap:4px;
  background:var(--c-faint);border:1px solid var(--c-border);border-radius:12px;padding:4px;}
.sts-seg button{min-height:46px;border:none;border-radius:9px;cursor:pointer;background:transparent;
  padding:0 10px;font-size:13.5px;font-weight:700;color:var(--c-muted);font-family:inherit;
  white-space:nowrap;overflow:hidden;text-overflow:ellipsis;
  transition:all .18s ease;}
.sts-seg button.on{background:var(--c-surface);color:var(--c-green);font-weight:800;
  box-shadow:0 1px 2px rgba(2,8,23,.1);}
:root[data-theme="dark"] .sts-seg button.on{background:var(--c-borderLight);}

.sts-note{display:flex;gap:9px;align-items:flex-start;
  font-size:12px;color:var(--c-muted);font-weight:600;line-height:1.5;
  background:var(--c-amberLight);border:1px solid var(--c-amberBorder);
  border-radius:12px;padding:11px 13px;margin:2px 0 4px;}
.sts-note svg{flex:none;margin-top:1px;color:var(--c-amber);}

.sts-foot{display:flex;gap:10px;margin-top:18px;}
.sts-cancel{flex:1;min-height:48px;border-radius:13px;cursor:pointer;
  background:var(--c-faint);border:1px solid var(--c-border);color:var(--c-slate);
  font-size:14px;font-weight:700;font-family:inherit;
  transition:transform .12s ease, background .15s ease;}
.sts-cancel:active{transform:scale(.97);background:var(--c-border);}
.sts-save{flex:1.4;min-height:48px;border:none;border-radius:13px;cursor:pointer;
  display:flex;align-items:center;justify-content:center;gap:8px;
  background:linear-gradient(180deg,var(--c-green) 0%,var(--c-greenDark) 100%);
  color:#fff;font-size:14px;font-weight:800;font-family:inherit;
  box-shadow:0 4px 12px color-mix(in srgb, var(--c-green) 35%, transparent);
  transition:transform .15s ease, filter .15s ease, opacity .15s ease;}
.sts-save:active{transform:scale(.97);filter:brightness(1.08);}
.sts-save:disabled{opacity:.45;cursor:default;transform:none;box-shadow:none;}

/* ---- printer section ---- */
.sts-printer-empty{display:flex;flex-direction:column;align-items:center;text-align:center;gap:8px;
  padding:22px 14px;border-radius:14px;background:var(--c-faint);border:1px dashed var(--c-border);}
.sts-printer-empty svg{color:var(--c-mutedLight);}
.sts-printer-empty p{margin:0;font-size:12.5px;font-weight:600;color:var(--c-muted);line-height:1.5;}
.sts-printer-empty-cta{min-height:44px;padding:0 18px;border-radius:12px;border:none;cursor:pointer;
  background:var(--c-green);color:#fff;font-size:13px;font-weight:800;font-family:inherit;margin-top:4px;}
.sts-printer-empty-cta:active{transform:scale(.97);}

.sts-printer-roles{display:flex;flex-direction:column;}
.sts-printer-role-section{margin-bottom:18px;}
.sts-printer-role-section:last-child{margin-bottom:0;}

.sts-printer-list{display:flex;flex-direction:column;gap:8px;}
.sts-printer-item{display:flex;align-items:center;justify-content:space-between;gap:10px;
  min-height:52px;padding:9px 13px;border-radius:13px;cursor:pointer;text-align:start;
  background:var(--c-surface);border:1.5px solid var(--c-border);width:100%;box-sizing:border-box;
  font-family:inherit;transition:border-color .15s ease, background .15s ease, transform .1s ease;}
.sts-printer-item:active{transform:scale(.98);}
/* Row = select button + delete. The select target must stay full-width-ish:
   a mis-tap that deletes a printer mid-service is far worse than one that
   selects the wrong one, so delete is deliberately the smaller target and
   asks for confirmation. */
.sts-printer-entry{display:flex;align-items:stretch;gap:8px;width:100%;box-sizing:border-box;min-width:0;}
/* width:100% on .sts-printer-item is correct when it IS the row, but inside
   one it claims the whole row and pushes the delete button outside the panel,
   which then reflowed the labels above it. Flex sizing must win here.
   (No backticks in here: this CSS lives inside a JS template literal.) */
.sts-printer-entry .sts-printer-item{flex:1 1 auto;width:auto;min-width:0;}
.sts-printer-del{flex:0 0 auto;min-width:44px;min-height:44px;border-radius:12px;cursor:pointer;
  border:1px solid var(--c-redBorder);background:var(--c-redLight);color:var(--c-red);
  font-size:16px;font-weight:800;line-height:1;display:flex;align-items:center;justify-content:center;}
.sts-printer-del:disabled{opacity:.5;cursor:default;}
.sts-printer-item.on{border-color:var(--c-green);background:var(--c-greenLight);}
.sts-printer-item-name{display:block;font-size:13.5px;font-weight:800;color:var(--c-ink);}
.sts-printer-item-meta{display:block;font-size:11.5px;font-weight:600;color:var(--c-muted);margin-top:2px;}
.sts-printer-check{flex:none;color:var(--c-green);}

/* KDS printers — one summary row per category, each expanding on its own.
   The row shows where that category prints WITHOUT being opened, because the
   common task is checking, not changing. */
.sts-kds-printers{margin-top:14px;padding-top:12px;border-top:1px solid var(--c-border);}
.sts-cat{border:1px solid var(--c-border);border-radius:11px;margin-top:8px;overflow:hidden;
  background:var(--c-surface);}
.sts-cat-row{width:100%;min-height:52px;display:flex;align-items:center;gap:10px;
  padding:0 12px;border:none;background:transparent;cursor:pointer;font-family:inherit;
  text-align:left;transition:background .15s ease;}
.sts-cat-row:active{background:var(--c-faint);}
.sts-cat-name{flex:1;min-width:0;font-size:13.5px;font-weight:700;color:var(--c-ink);
  overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
/* An unassigned category is a valid setup, not a fault — muted, never red. */
.sts-cat-to{flex:none;max-width:45%;font-size:12px;font-weight:600;color:var(--c-slate);
  overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.sts-cat-to.fallback{color:var(--c-muted);font-style:italic;}
.sts-cat-chev{flex:none;color:var(--c-mutedLight);font-size:15px;line-height:1;}
.sts-cat-body{padding:2px 12px 12px;border-top:1px solid var(--c-border);}
.sts-cat-body .sts-printer-role-section .sts-label{display:none;}

.sts-printer-add-link{min-height:44px;display:flex;align-items:center;justify-content:center;gap:7px;
  width:100%;box-sizing:border-box;border-radius:12px;border:1.5px dashed var(--c-border);
  background:transparent;cursor:pointer;color:var(--c-green);font-size:13px;font-weight:800;
  font-family:inherit;margin-top:8px;}

.sts-printer-actions{display:flex;gap:10px;margin-top:12px;}
.sts-printer-action-btn{flex:1;min-height:44px;border-radius:12px;cursor:pointer;
  background:var(--c-faint);border:1px solid var(--c-border);color:var(--c-text);
  font-size:12.5px;font-weight:700;font-family:inherit;
  display:flex;align-items:center;justify-content:center;gap:6px;transition:transform .12s ease;}
.sts-printer-action-btn:active{transform:scale(.97);}
.sts-printer-action-btn:disabled{opacity:.45;cursor:default;transform:none;}

.sts-printer-result{display:flex;gap:8px;align-items:flex-start;padding:10px 12px;border-radius:12px;
  font-size:12px;font-weight:600;line-height:1.5;margin-top:10px;}
.sts-printer-result.success{background:var(--c-successLight);border:1px solid transparent;color:var(--c-success);}
.sts-printer-result.warn{background:var(--c-amberLight);border:1px solid var(--c-amberBorder);color:var(--c-amber);}
.sts-printer-result.error{background:var(--c-redLight);border:1px solid var(--c-redBorder);color:var(--c-redText);}

.sts-printer-form{margin-top:2px;}
.sts-printer-form-actions{display:flex;gap:10px;margin-top:6px;}

/* ---- danger zone ---- */
.sts-danger{margin-top:18px;padding-top:16px;border-top:1px solid var(--c-border);}
.sts-reset{width:100%;min-height:48px;border-radius:13px;cursor:pointer;
  display:flex;align-items:center;justify-content:center;gap:8px;
  background:var(--c-redLight);border:1px solid var(--c-redBorder);color:var(--c-redText);
  font-size:13.5px;font-weight:800;font-family:inherit;
  transition:transform .12s ease, filter .15s ease;}
.sts-reset:active{transform:scale(.97);filter:brightness(.97);}
.sts-confirm-text{font-size:12.5px;font-weight:600;color:var(--c-redText);line-height:1.55;margin:0 0 12px;
  background:var(--c-redLight);border:1px solid var(--c-redBorder);border-radius:12px;padding:11px 13px;}
.sts-confirm-row{display:flex;gap:10px;}
.sts-confirm-yes{flex:1.4;min-height:48px;border:none;border-radius:13px;cursor:pointer;
  display:flex;align-items:center;justify-content:center;gap:8px;
  background:var(--c-red);color:#fff;font-size:13.5px;font-weight:800;font-family:inherit;
  box-shadow:0 4px 12px color-mix(in srgb, var(--c-red) 35%, transparent);
  transition:transform .12s ease, filter .15s ease, opacity .15s ease;}
.sts-confirm-yes:active{transform:scale(.97);filter:brightness(1.08);}
.sts-confirm-yes:disabled{opacity:.45;cursor:default;transform:none;box-shadow:none;}

@media (min-width:768px){
  .sts-panel{width:440px;border-radius:24px;padding:26px;}
  .sts-head h3{font-size:18px;}
  .sts-key{min-height:64px;font-size:23px;}
  .sts-seg button{min-height:48px;font-size:14px;}
}
`;

/* ------------------------------------------------------------------ */
/* Inline SVG icons (stroke = currentColor)                            */
/* ------------------------------------------------------------------ */
const Icon = {
    close: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M18 6 6 18M6 6l12 12" />
        </svg>
    ),
    backspace: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 4H8l-5 8 5 8h13a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1z" />
            <path d="m12 9 6 6M18 9l-6 6" />
        </svg>
    ),
    enter: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M5 12h14M13 6l6 6-6 6" />
        </svg>
    ),
    alert: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 8v4M12 16h.01" />
        </svg>
    ),
    warn: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 9v4M12 17h.01" />
            <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
        </svg>
    ),
};

function Spinner() {
    return (
        <svg className="sts-spin" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true">
            <path d="M21 12a9 9 0 1 1-6.2-8.56" />
        </svg>
    );
}

// isManagerRole is still used by the tenant username/password fallback below,
// which validates client-side against /auth/tenant-login. The PIN path's role
// check now happens server-side inside /manager-approvals/verify.

/* ------------------------------------------------------------------ */
/* Sheet                                                               */
/* ------------------------------------------------------------------ */
export default function StationSettingsSheet({ open, onClose }) {
    const { tenantId } = useAuth();
    const { t } = useLanguage();
    const { config, saveStation, clearStation } = useStation();

    // stage: 'gate' → 'settings'
    const [stage, setStage] = useState('gate');
    const [gateMode, setGateMode] = useState('pin'); // 'pin' | 'password'
    // PIN entry state lives in <ManagerGate/>. What remains here belongs to
    // the tenant username/password fallback only.
    const [verifying, setVerifying] = useState(false);
    const [gateError, setGateError] = useState('');

    // password fallback
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');

    // settings
    const [station, setStation] = useState(STATIONS.CASHIER);
    const [kdsQueue, setKdsQueue] = useState('kitchen');
    const [printerIds, setPrinterIds] = useState(DEFAULT_PRINTER_IDS);
    // The tenant's kitchen-display categories. Each is both a KDS queue a
    // device can serve and a printer slot it can be assigned — the same list
    // the web Functions page already uses to build its KDS tiles
    // (FunctionsPage.js:141). Left empty on failure: both consumers below
    // fall back to the legacy kitchen/bar pair rather than showing nothing.
    const [kdsCategories, setKdsCategories] = useState([]);

    useEffect(() => {
        let cancelled = false;
        categoryService.getAll()
            .then((cats) => {
                if (cancelled) return;
                const names = (cats || [])
                    .filter((c) => c && c.is_kitchen_display)
                    .map((c) => c.name)
                    .filter(Boolean)
                    .sort((a, b) => a.localeCompare(b));
                setKdsCategories([...new Set(names)]);
            })
            .catch(() => { if (!cancelled) setKdsCategories([]); });
        return () => { cancelled = true; };
        // Keyed on `open`, not mounted once: this sheet lives for the whole
        // session inside every station shell, so a single failed or empty
        // fetch at boot used to degrade it to the legacy kitchen/bar pair
        // permanently — existing per-category printers invisible, new ones
        // impossible to create, until the app was restarted.
    }, [open]);
    const [confirmReset, setConfirmReset] = useState(false);
    const [saving, setSaving] = useState(false);
    const [resetting, setResetting] = useState(false);
    const [saveError, setSaveError] = useState('');

    // Reset everything each time the sheet opens, seeding stage-2 values from
    // the current station config.
    useEffect(() => {
        if (!open) return;
        setStage('gate');
        setGateMode('pin');
        setVerifying(false);
        setGateError('');
        setUsername('');
        setPassword('');
        setStation(config?.station || STATIONS.CASHIER);
        setKdsQueue(config?.kdsQueue || 'kitchen');
        setPrinterIds(config?.printerIds || DEFAULT_PRINTER_IDS);
        setConfirmReset(false);
        setSaving(false);
        setResetting(false);
        setSaveError('');
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps


    // ---- gate: PIN --------------------------------------------------
    // Owned entirely by the shared <ManagerGate/> (components/pos/
    // ManagerGate.js), which authenticates through POST
    // /manager-approvals/verify — an endpoint that mints no token, so a
    // manager's approval can never replace this device's operator session.
    // The pad, its auto-submit, its keyboard handling and its error states
    // all live there now; this sheet only reacts to onApprove.

    // ---- gate: password fallback ------------------------------------
    // Validates via POST /auth/tenant-login directly on the axios instance.
    // Deliberately NOT authService.tenantLogin / AuthContext.login — both
    // persist token + tenant state to localStorage and would replace this
    // device's session. Here the response is only inspected (belongs to this
    // device's tenant, and its role is manager/admin — the null-role→'admin'
    // backend default legitimately passes) and then discarded.
    const submitPassword = useCallback(async (e) => {
        e.preventDefault();
        if (verifying) return;
        if (!username.trim() || !password) {
            setGateError(t('all_fields_required'));
            return;
        }
        setVerifying(true);
        setGateError('');
        try {
            const res = await api.post('/auth/tenant-login', {
                username: username.trim(),
                password,
            });
            if (!res?.data?.access_token || res.data.tenant_id !== tenantId) {
                setGateError(t('invalid_credentials'));
            } else if (!isManagerRole(res.data.user_role)) {
                setGateError(t('manager_access_required'));
            } else {
                setPassword('');
                setStage('settings');
            }
        } catch (err) {
            setGateError(t('invalid_credentials'));
        } finally {
            setVerifying(false);
        }
    }, [verifying, username, password, tenantId, t]);

    // ---- settings ----------------------------------------------------
    const pickStation = (id) => {
        setStation(id);
        setSaveError('');
    };

    // Merges one role's change onto the existing map rather than replacing
    // it outright — StationSettingsPrinter reports one role at a time
    // (Task 7), and this must never clobber the other two roles' selections
    // just because only one was touched.
    const handlePrinterChange = (roleKey, printerIdForRole) => {
        setPrinterIds((prev) => ({ ...(prev || DEFAULT_PRINTER_IDS), [roleKey]: printerIdForRole }));
    };

    const handleSave = async () => {
        if (saving) return;
        setSaving(true);
        setSaveError('');
        try {
            // Preserve whatever backendEnv the device's config already carries
            // (e.g. an internal QC device configured to target staging) rather
            // than silently flipping it back to 'prod' on a routine save —
            // this sheet has no UI to change it. Same for tenantId — it's the
            // belt-and-braces fallback set at enrollment (see stationConfig.js)
            // and has no UI here either.
            await saveStation({
                station,
                kdsQueue: station === STATIONS.KDS ? kdsQueue : null,
                printerIds,
                backendEnv: config?.backendEnv,
                tenantId: config?.tenantId,
            });
            onClose();
        } catch (err) {
            setSaveError(t('enroll_save_failed'));
        } finally {
            setSaving(false);
        }
    };

    const handleReset = async () => {
        if (resetting) return;
        setResetting(true);
        try {
            await clearStation(); // PosAppShell re-renders into EnrollmentScreen
            onClose();
        } catch (err) {
            setResetting(false);
            setSaveError(t('enroll_save_failed'));
        }
    };

    // ---- keyboard: Escape closes ------------------------------------
    // Digit/Backspace handling moved into ManagerGate along with the pad.
    useEffect(() => {
        if (!open) return undefined;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [open, onClose]);

    if (!open) return null;

    const stationOptions = [
        { id: STATIONS.CASHIER, name: t('cashier') },
        { id: STATIONS.TABLE_SERVICE, name: t('table_service') },
        { id: STATIONS.WAITER_SALES, name: t('waiter_sales') },
        { id: STATIONS.KDS, name: t('kds') },
    ];
    // One queue per kitchen-display category, matching the web Functions
    // page. Falls back to the legacy pair so a failed category fetch cannot
    // leave a KDS device with nothing to select.
    const queueOptions = kdsCategories.length
        ? kdsCategories.map((name) => ({ id: name, name }))
        : [
            { id: 'kitchen', name: t('kitchen') },
            { id: 'bar', name: t('bar') },
        ];
    const gateErrorView = gateError ? (
        <div className="sts-error" role="alert">
            {Icon.alert}
            <span>{gateError}</span>
        </div>
    ) : null;

    return createPortal(
        <div className="sts-overlay" role="dialog" aria-modal="true" aria-label={t('station_settings')}>
            <style>{CSS}</style>
            <div className="sts-scrim" onClick={onClose} />
            <div className="sts-panel">
                <div className="sts-head">
                    <h3>{t('station_settings')}</h3>
                    <button type="button" className="sts-close" onClick={onClose} aria-label={t('close')}>
                        {Icon.close}
                    </button>
                </div>

                {stage === 'gate' ? (
                    <div>
                        <p className="sts-sub">{t('station_settings_managers_only')}</p>
                        {gateErrorView}

                        {gateMode === 'pin' ? (
                            <div>
                                {/* The PIN pad is the shared ManagerGate — see its
                                    header for why approvals must never mint a
                                    token. Device config is audited under the
                                    'device_config' action. */}
                                <ManagerGate
                                    open
                                    action="device_config"
                                    onApprove={() => setStage('settings')}
                                    onCancel={onClose}
                                />
                                <button type="button" className="sts-switch" onClick={() => { setGateMode('password'); setGateError(''); }}>
                                    {t('use_manager_password')}
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={submitPassword} noValidate>
                                <div className="sts-field">
                                    <label className="sts-label" htmlFor="sts-username">{t('username')}</label>
                                    <input
                                        id="sts-username"
                                        className="sts-input"
                                        type="text"
                                        placeholder={t('enter_username')}
                                        value={username}
                                        onChange={(e) => setUsername(e.target.value)}
                                        autoComplete="username"
                                        autoCapitalize="none"
                                        autoCorrect="off"
                                        spellCheck="false"
                                        disabled={verifying}
                                        required
                                    />
                                </div>
                                <div className="sts-field" style={{ marginBottom: 16 }}>
                                    <label className="sts-label" htmlFor="sts-password">{t('password')}</label>
                                    <input
                                        id="sts-password"
                                        className="sts-input"
                                        type="password"
                                        placeholder={t('enter_password')}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        autoComplete="current-password"
                                        disabled={verifying}
                                        required
                                    />
                                </div>
                                <button className="sts-btn" type="submit" disabled={verifying}>
                                    {verifying ? <Spinner /> : null}
                                    {t('verify')}
                                </button>
                                <button type="button" className="sts-switch" onClick={() => { setGateMode('pin'); setGateError(''); }}>
                                    {t('use_pin_instead')}
                                </button>
                            </form>
                        )}
                    </div>
                ) : (
                    <div>
                        <div className="sts-row" style={{ marginTop: 14 }}>
                            <div className="sts-label">{t('station')}</div>
                            <div className="sts-seg" role="group" aria-label={t('station')}>
                                {stationOptions.map((opt) => (
                                    <button
                                        key={opt.id}
                                        type="button"
                                        className={station === opt.id ? 'on' : ''}
                                        onClick={() => pickStation(opt.id)}
                                        aria-pressed={station === opt.id}
                                    >
                                        {opt.name}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Each station shows only its own half. A KDS is a
                            screen and needs a queue, not printers; a selling
                            station is the reverse. Both used to render at
                            once, with the queue merely greyed out — which was
                            already long and became unusable once printers
                            went one-per-category. */}
                        {station === STATIONS.KDS ? (
                            <div className="sts-row">
                                <div className="sts-label">{t('kds_queue')}</div>
                                <div className="sts-seg" role="group" aria-label={t('kds_queue')}>
                                    {queueOptions.map((q) => (
                                        <button
                                            key={q.id}
                                            type="button"
                                            className={kdsQueue === q.id ? 'on' : ''}
                                            onClick={() => { setKdsQueue(q.id); setSaveError(''); }}
                                            aria-pressed={kdsQueue === q.id}
                                        >
                                            {q.name}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <StationSettingsPrinter
                                printerIds={printerIds}
                                onChange={handlePrinterChange}
                                categories={kdsCategories}
                            />
                        )}

                        <div className="sts-note">
                            {Icon.warn}
                            <span>{t('station_change_warning')}</span>
                        </div>

                        {saveError && (
                            <div className="sts-error" role="alert" style={{ marginTop: 12, marginBottom: 0 }}>
                                {Icon.alert}
                                <span>{saveError}</span>
                            </div>
                        )}

                        <div className="sts-foot">
                            <button type="button" className="sts-cancel" onClick={onClose} disabled={saving || resetting}>
                                {t('cancel')}
                            </button>
                            <button type="button" className="sts-save" onClick={handleSave} disabled={saving || resetting}>
                                {saving ? <Spinner /> : null}
                                {saving ? t('saving') : t('save')}
                            </button>
                        </div>

                        <div className="sts-danger">
                            {confirmReset ? (
                                <div>
                                    <p className="sts-confirm-text">{t('reset_device_confirm')}</p>
                                    <div className="sts-confirm-row">
                                        <button type="button" className="sts-cancel" onClick={() => setConfirmReset(false)} disabled={resetting}>
                                            {t('cancel')}
                                        </button>
                                        <button type="button" className="sts-confirm-yes" onClick={handleReset} disabled={resetting}>
                                            {resetting ? <Spinner /> : null}
                                            {t('reset_device_enrollment')}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <button type="button" className="sts-reset" onClick={() => setConfirmReset(true)} disabled={saving}>
                                    {t('reset_device_enrollment')}
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
}
